/**
 * sentinelService — SENTINEL-CORE-1 orchestrator
 *
 * Single entry point for governed call wrappers to apply all Sentinel
 * substrate operations in one fire-and-forget call:
 *   1. tagAgentAction (agentIdentityService) — F-SENTINEL-IDENTITY-001
 *   2. recordLineage (semanticLineageService) — F-SENTINEL-LINEAGE-001
 *   3. updateTrust (trustLatticeService) — F-SENTINEL-TRUST-001
 *   4. KDW ingestion (kdwService) — F-SENTINEL-KDW-001
 *   5. declareIntent (agentGatewayClient) — SEC-14 / F-AGENT-GATEWAY-INTENT-001
 *
 * Usage in governed call wrappers (fire-and-forget, KTRUST-L1 pattern):
 *
 *   import { sentinelTag } from '../sentinel/sentinelService.js';
 *   // ... after getting result ...
 *   sentinelTag({ tenantId, agentId, callType: 'aegis', module, decisionType, result, userId })
 *     .catch(err => console.warn('[sentinelService] tag failed (non-fatal):', err.message));
 *
 * sentinelTag never throws to the caller — all sub-service errors are caught
 * and logged (KTRUST-L1 resilience principle).
 *
 * SEC-14 / KTRUST-L1 intent declaration:
 *   declareIntent is fired in parallel with sentinelTag — it does NOT block the
 *   governed call. It is a sidecar that runs concurrently with the identity tag.
 *   If intent declaration fails it is non-fatal (logged only).
 */

import { tagAgentAction } from './agentIdentityService';
import { recordLineage } from './semanticLineageService';
import { updateTrust } from './trustLatticeService';
import { ingestBatch } from './kdwService';
import { declareIntent } from './agentGatewayClient';

/**
 * Apply all Sentinel substrate operations for a governed call result.
 *
 * @param {object} opts
 * @param {string} opts.tenantId
 * @param {string} opts.agentId
 * @param {string} opts.callType      - 'aegis' | 'artemis' | 'council'
 * @param {string} opts.module
 * @param {string} opts.decisionType
 * @param {object} opts.inputParams   - original call params (for lineage hashing)
 * @param {object} opts.result        - governed call result
 * @param {string|null} [opts.userId]
 * @param {string} [opts.intentSummary] - SEC-14: optional intent summary for gateway declaration
 * @returns {Promise<void>}
 */
export async function sentinelTag(opts) {
  const {
    tenantId, agentId, callType, module: mod, decisionType,
    inputParams, result, userId = null,
    intentSummary = '',
  } = opts;

  // ── SEC-14: Intent declaration (KTRUST-L1 fire-and-forget, parallel) ─────
  // Fires concurrently — never awaited, never blocks governed call result.
  const _intentSummary = intentSummary || `${callType}:${mod}:${decisionType}`;
  declareIntent({
    tenantId, agentId, callType, module: mod, decisionType,
    intentSummary: _intentSummary,
  }).catch(err =>
    console.warn('[sentinelService] declareIntent failed (non-fatal):', err?.message)
  );

  let actionId = null;
  let lineageId = null;
  let trustResult = null;

  try {
    actionId = await tagAgentAction({
      tenantId, agentId, callType, module: mod, decisionType,
      decisionId: result?.decisionId || null,
      userId,
    });
  } catch (err) {
    console.warn('[sentinelService] tagAgentAction failed (non-fatal):', err?.message);
  }

  try {
    lineageId = await recordLineage({
      tenantId, agentId, callType, module: mod, decisionType, inputParams, result,
    });
  } catch (err) {
    console.warn('[sentinelService] recordLineage failed (non-fatal):', err?.message);
  }

  try {
    trustResult = await updateTrust({ tenantId, agentId, module: mod, decisionType, result });
  } catch (err) {
    console.warn('[sentinelService] updateTrust failed (non-fatal):', err?.message);
  }

  // KDW ingestion — batch the records produced by the three sub-services.
  const kdwRecords = [];
  const now = new Date().toISOString();

  if (actionId) {
    kdwRecords.push({
      tenantId,
      recordType: 'agent_action',
      sourceRef: `sentinel_agent_actions/${tenantId}/records/${actionId}`,
      summary: { agentId, callType, module: mod, decisionType },
      timestamp: now,
    });
  }

  if (lineageId) {
    kdwRecords.push({
      tenantId,
      recordType: 'lineage_record',
      sourceRef: `sentinel_lineage/${tenantId}/records/${lineageId}`,
      summary: { agentId, finalDisposition: result?.finalDisposition },
      timestamp: now,
    });
  }

  if (trustResult) {
    kdwRecords.push({
      tenantId,
      recordType: 'trust_update',
      sourceRef: `sentinel_trust_lattice/${tenantId}/agents/${agentId}`,
      summary: { agentId, newScore: trustResult.newScore, delta: trustResult.delta },
      timestamp: now,
    });
  }

  if (kdwRecords.length > 0) {
    ingestBatch(kdwRecords).catch(err =>
      console.warn('[sentinelService] KDW ingest failed (non-fatal):', err?.message)
    );
  }
}

export default { sentinelTag };
